<template>
    <div class="rtb-campaign-form">
        <div class="rtb-campaign-form__inner">
            <RtbFormHeader
                :title="pageTitle"
                :is-read-only="isReadOnly"
                :is-draft="isCurrentlyDraft"
                :display-status="displayStatus"
                :display-status-label="displayStatusLabel"
                :status-hint="statusHint"
                :will-remoderate="willRemoderate"
            />

            <form class="form" @submit.prevent="submit">
                <div v-if="!isEditMode && canWrite" class="rtb-form-import">
                    <button
                        type="button"
                        class="btn btn--secondary"
                        :disabled="importingSettings || !framesReady"
                        @click="triggerSettingsImport"
                    >
                        {{ importingSettings ? 'Загрузка…' : 'Загрузить настройки' }}
                    </button>
                    <input
                        ref="settingsFileInput"
                        type="file"
                        accept=".json,application/json"
                        class="visually-hidden"
                        @change="onSettingsFileSelected"
                    />
                </div>

                <RtbFormIdentity
                    :form="form"
                    :errors="errors"
                    :advertisers="advertisers"
                    :brands="filteredBrands"
                    :disabled="!canWrite"
                    :is-edit="isEditMode"
                    :is-admin="isAdmin"
                    @advertiser-change="onAdvertiserChange"
                />

                <RtbFormPeriod
                    :form="form"
                    :errors="errors"
                    :disabled="!canWrite"
                    :is-edit="isEditMode"
                    :today="today"
                    :start-locked="isStartDateLocked"
                    :end-min="endDateMin"
                >
                    <template #schedule>
                        <RtbScheduleGrid
                            :model-value="campaignSchedule"
                            :start-date="form.start_date"
                            :end-date="form.end_date"
                            :editable="canWrite"
                            :is-admin="isAdmin"
                            :campaign-id="campaign?.id || null"
                            :initial-frequencies="matrix.campaignFrequencies.value"
                            :show-apply-day-buttons="isEditMode && isAdmin && !!campaign?.id"
                            :applying-day="matrix.applyingDayIso.value"
                            @update:modelValue="campaignSchedule = $event"
                            @frequencies-updated="matrix.onCampaignFrequenciesUpdated"
                            @apply-day="matrix.onApplyCampaignDay"
                        />
                    </template>
                </RtbFormPeriod>

                <RtbFormCreatives
                    v-model="selectedCreativeIds"
                    :creatives="allCreatives"
                    :errors="errors"
                    :disabled="!canWrite"
                    :show-max-bid="true"
                    :bids="matrix.creativeBids"
                    :show-readiness-hint="showCreativeReadinessHint"
                    :is-selected="isCreativeSelected"
                    :is-paused="isCreativePausedInCampaign"
                    :is-ready="isCreativeReadyForImpressions"
                    :readiness-tooltip="getCreativeReadinessTooltip"
                    @set-bid="matrix.setCreativeBid"
                />

                <div :data-field="isPoi ? 'poi_layer_id' : undefined">
                <RtbFormScreens
                    :title="isPoi ? 'POI-слой' : 'Экраны'"
                    :selected-gids="selectedFrameGids"
                    :exporting="matrix.exportingScreens.value"
                    :loading="framesLoading || (isEditMode && !matrix.editMapReady.value && frames.length > 0)"
                    :show-map="isPoi || (framesReady && matrix.showFrameMap.value)"
                    :notice="uncoveredScreensNotice"
                    :errors="screenErrors"
                    :hint="isPoi ? '' : undefined"
                    :empty-text="framesFailed ? 'Не удалось загрузить экраны.' : 'Нет доступных экранов.'"
                    @export-screens="matrix.exportSelectedScreens"
                >
                    <GoogleMap
                        :frames="frames"
                        v-model:selectedFrameGids="selectedFrameGids"
                        :sidebar-mode="isPoi ? 'layers' : 'screens'"
                        :layers="layerOptions"
                        :selected-layer-id="form.poi_layer_id"
                        :layer-locked="isEditMode || !canWrite"
                        :layer-details="layerCache"
                        :points="mapPoints"
                        :disable-price-check="true"
                        :hide-required-star="true"
                        :selected-creatives="selectedCreativeObjects"
                        :allow-multi-ssp="true"
                        :multi-filters="true"
                        :show-organization-filter="isAdmin || isAgency"
                        :read-only="!canWrite || isPoi"
                        :can-manage-test-frames="isAdmin"
                        :show-week-requests="true"
                        @select-layer="onSelectLayer"
                        @expand-layer="onExpandLayer"
                    />
                </RtbFormScreens>
                </div>

                <RtbFormLimits
                    v-model:distribute-evenly="distributeEvenly"
                    :form="form"
                    :errors="errors"
                    :disabled="!canWrite"
                    :has-limit="hasAtLeastOneLimit"
                    :days="campaignDays"
                />

                <RtbFormBidMatrix
                    v-if="selectedCreativeIds.length > 0 && selectedFrameGids.length > 0"
                    :matrix="matrix"
                    :errors="errors"
                    :audience-enabled="audienceEnabled"
                    :is-edit="isEditMode"
                    :can-write="canWrite"
                    :is-admin="isAdmin"
                    :campaign="campaign"
                    :form="form"
                    :campaign-schedule="campaignSchedule"
                />

                <AudienceDataOption
                    :enabled="audienceEnabled"
                    :locked="audienceLocked"
                    :has-beeline="hasBeelineSelected"
                    :can-enable="!isEditMode || isCurrentlyDraft"
                    :can-write="canWrite"
                    :markup-percent="audienceMarkupPercent"
                    :offer-url="audienceOfferUrl"
                    :payable-hint="audiencePayableHint"
                    :preview="audiencePreview"
                    @update:enabled="onAudienceEnabled"
                />

                <RtbFormFooter
                    :can-write="canWrite"
                    :can-submit="isValid"
                    :can-draft="canSaveDraft"
                    :show-draft="!isEditMode || isCurrentlyDraft"
                    :is-edit="isEditMode"
                    :busy="saving"
                    :as-draft="savingAsDraft"
                    :exporting="exportingSettings"
                    :general-error="generalError"
                    @draft="submitDraft"
                    @export-settings="exportSettings"
                    @cancel="confirmLeave(cancelUrl)"
                />
            </form>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import axios from 'axios';
import GoogleMap from '../../common/GoogleMap.vue';
import AudienceDataOption from './AudienceDataOption.vue';
import RtbScheduleGrid from './RtbScheduleGrid.vue';
import RtbFormHeader from './form/RtbFormHeader.vue';
import RtbFormIdentity from './form/RtbFormIdentity.vue';
import RtbFormPeriod from './form/RtbFormPeriod.vue';
import RtbFormCreatives from './form/RtbFormCreatives.vue';
import RtbFormScreens from './form/RtbFormScreens.vue';
import RtbFormLimits from './form/RtbFormLimits.vue';
import RtbFormBidMatrix from './form/RtbFormBidMatrix.vue';
import RtbFormFooter from './form/RtbFormFooter.vue';
import { useRtbCampaignForm } from '../../../composables/useRtbCampaignForm';
import { useRtbBidMatrix } from '../../../composables/useRtbBidMatrix';
import { useToast } from '../../../composables/useToast';
import { RTB_CAMPAIGN_TYPE } from '../../../constants/rtbCampaigns';

const props = defineProps({
    campaign: { type: Object, default: null },
    userData: { type: Object, default: () => ({}) },
    advertisers: { type: Array, default: () => [] },
    frames: { type: Array, default: () => [] },
    availableCreatives: { type: Array, default: () => [] },
    campaignCreatives: { type: Array, default: () => [] },
    campaignFrameSettings: { type: Array, default: () => [] },
    initialSelectedFrameGids: { type: Array, default: () => [] },
    initialFrameFrequenciesMap: { type: Object, default: () => ({}) },
    initialCampaignFrequencies: { type: Array, default: () => [] },
    audienceConfig: { type: Object, default: () => ({ markup_percent: 10, offer_url: '#' }) },
    framesUrl: { type: String, default: '' },
    type: { type: String, default: RTB_CAMPAIGN_TYPE.MANUAL },
    poiLayers: { type: Array, default: () => [] },
    selectedLayerId: { type: [Number, String], default: null },
});

const toast = useToast();
const selectedFrameGids = ref([]);
const skipCatalog = props.type === RTB_CAMPAIGN_TYPE.POI;
const frames = ref(props.framesUrl && !skipCatalog ? [] : (props.frames || []));
const framesLoading = ref(Boolean(props.framesUrl) && !skipCatalog);
const framesReady = ref(!props.framesUrl || skipCatalog);
const framesFailed = ref(false);
const layerCache = ref({});
const layerLoads = new Map();
const matrixApi = {};

const {
    form,
    errors,
    generalError,
    saving,
    savingAsDraft,
    exportingSettings,
    importingSettings,
    isCurrentlyDraft,
    selectedCreativeIds,
    campaignSchedule,
    audienceEnabled,
    audienceLocked,
    audiencePreview,
    distributeEvenly,
    settingsFileInput,
    today,
    canWrite,
    isReadOnly,
    isEditMode,
    isAdmin,
    isAgency,
    pageTitle,
    cancelUrl,
    isStartDateLocked,
    endDateMin,
    filteredBrands,
    allCreatives,
    campaignDays,
    hasAtLeastOneLimit,
    selectedCreativeObjects,
    uncoveredScreensNotice,
    showCreativeReadinessHint,
    hasBeelineSelected,
    audienceMarkupPercent,
    audienceOfferUrl,
    audiencePayableHint,
    displayStatus,
    displayStatusLabel,
    statusHint,
    willRemoderate,
    canSaveDraft,
    isValid,
    submit,
    submitDraft,
    exportSettings,
    triggerSettingsImport,
    onSettingsFileSelected,
    onAdvertiserChange,
    onAudienceEnabled,
    isCreativeSelected,
    isCreativePausedInCampaign,
    isCreativeReadyForImpressions,
    getCreativeReadinessTooltip,
    resolveCreative,
    confirmLeave,
    markDirty,
} = useRtbCampaignForm(props, {
    selectedFrameGids,
    frames,
    extraPayload: (...args) => matrixApi.extraPayload?.(...args) || {},
    extraValidate: (...args) => matrixApi.extraValidate?.(...args) || {},
    onHydrated: () => matrixApi.hydrateBindings?.(),
    onSettingsApplied: payload => matrixApi.applyImportedBindings?.(payload),
});

const isPoi = computed(() => props.type === RTB_CAMPAIGN_TYPE.POI);
const layerOptions = computed(() => {
    const options = (props.poiLayers || []).map(layer => ({
        id: layer.id,
        name: layer.name,
        screens_count: Number(layer.screens_count ?? layer.resolved_frames_count ?? 0),
    }));
    const current = props.campaign?.poi_layer;
    if (current?.id && !options.some(layer => Number(layer.id) === Number(current.id))) {
        options.unshift({
            id: current.id,
            name: current.name,
            screens_count: Number(current.screens_count ?? props.initialSelectedFrameGids.length),
        });
    }
    return options;
});
const screensFollowLayer = computed(() => isPoi.value && (!props.campaign || props.campaign.status === 'draft'));
const screenErrors = computed(() => {
    const current = errors.value || {};
    if (!isPoi.value) return current;
    return {
        ...current,
        frame_gids: current.frame_gids || current.poi_layer_id || '',
    };
});
const mapPoints = computed(() => {
    if (!isPoi.value || !form.poi_layer_id) return [];
    const detail = layerCache.value[form.poi_layer_id] || layerCache.value[String(form.poi_layer_id)];
    return detail?.error ? [] : (detail?.points || []);
});

const rememberLayer = (id, data) => {
    layerCache.value = { ...layerCache.value, [id]: data };
};

const loadLayerPayload = (layerId) => {
    const id = Number(layerId);
    if (!id) return Promise.resolve(null);
    const cached = layerCache.value[id];
    if (cached && !cached.error) return Promise.resolve(cached);
    if (layerLoads.has(id)) return layerLoads.get(id);

    const request = axios.get(`/rtb/poi-layers/${id}/screens`)
        .then(({ data }) => {
            rememberLayer(id, data);
            return data;
        })
        .catch((error) => {
            rememberLayer(id, { error: true, frames: [], points: [], frame_gids: [] });
            throw error;
        })
        .finally(() => layerLoads.delete(id));

    layerLoads.set(id, request);
    return request;
};

const applyLayerSelection = async (layerId) => {
    if (!isPoi.value) return;
    if (!layerId) {
        frames.value = [];
        if (screensFollowLayer.value) selectedFrameGids.value = [];
        return;
    }
    try {
        const data = await loadLayerPayload(layerId);
        if (Number(form.poi_layer_id) !== Number(layerId)) return;
        frames.value = data?.frames || [];
        if (screensFollowLayer.value) selectedFrameGids.value = data?.frame_gids || [];
    } catch {
        if (Number(form.poi_layer_id) !== Number(layerId)) return;
        frames.value = [];
        if (screensFollowLayer.value) selectedFrameGids.value = [];
        toast.error('Не удалось загрузить слой');
    }
};

const onSelectLayer = (layer) => {
    if (isEditMode.value || !canWrite.value) return;
    form.poi_layer_id = Number(layer.id);
};

const onExpandLayer = (layerId) => {
    loadLayerPayload(layerId).catch(() => {
        if (Number(form.poi_layer_id) === Number(layerId)) return;
        toast.error('Не удалось загрузить слой');
    });
};

watch(() => form.poi_layer_id, (layerId) => {
    applyLayerSelection(layerId);
});

const matrix = useRtbBidMatrix({
    props,
    frames,
    selectedCreativeIds,
    selectedFrameGids,
    campaignSchedule,
    audienceEnabled,
    audiencePreview,
    isEditMode,
    canWrite,
    isAdmin,
    resolveCreative,
    markDirty,
});

Object.assign(matrixApi, matrix);

onMounted(async () => {
    if (isPoi.value && !form.poi_layer_id && props.selectedLayerId) {
        form.poi_layer_id = Number(props.selectedLayerId);
    } else if (isPoi.value && form.poi_layer_id) {
        await applyLayerSelection(form.poi_layer_id);
    }
    if (isPoi.value || !props.framesUrl) return;
    try {
        const { data } = await axios.get(props.framesUrl);
        frames.value = Array.isArray(data?.frames) ? data.frames : [];
        framesReady.value = true;
    } catch {
        framesFailed.value = true;
        toast.error('Не удалось загрузить экраны');
        frames.value = [];
    } finally {
        framesLoading.value = false;
    }
});
</script>

<style lang="scss">
@use '../../../../scss/components/rtb-campaign-form';
</style>
